import React, {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {SimpleSearchWithId, SearchByFilters, SearchByFiltersCustom} from "../../../../../reduxToolkit/Search/SearchApi";
import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import CustomTooltip from "../../../../../components/CustomTooltip";
import CustomChip from "../../../../../components/CustomChip";
import CustomImageTable from "../../../../../components/CustomImageTable";
import {statusBorderColor, statusColor, statusFontColor} from "../../../../../enums/statusColorEnum";
import {status} from "../../../../../enums/statusEnum";
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile";
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem";
import TableFunction from "../../../../../components/table/TableFunction";
import {CRITERIA_MODEL, SEARCH_MODEL} from "../../../../../components/table/Model/TableModel";
import {VIEW_TYPE} from "../../../../../enums/ViewType";
import {DATE_TIME_FORMAT_NAME_MODEL, getDateFormat, getTimeFormat} from "../../../../../Apis/functions";
import {
    DetailsIconsOptions,
    IconClockTable,
    IconScheduleTable,
    NextIconsOptions,
    PersonOutlinedChip
} from "../../../../../components/buttons/IconButtons";
import routes from "../../../../../Routes/routes";
import {
    getMaterialControlRequestTypeByType,
    getMaterialRequestTableStatusId,
    isVehicleMovement,
    MATERIAL_CONTROL_TABS,
    SUB_VIEW_MODEL
} from "../../Model/Model";
import {TABLES} from "../../../../../Apis/Tables";
import {colors} from "../../../../../enums/colors";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * Reads the material requests that already ended their approval, so the history
 * is only consulted and no decision is taken on it.
 */
export const MaterialRequestHistoryTableApp = ({
    option,
    setBodyForExport,
    searchElement,
    finalArray,
    modified,
    setModified,
    setCriteria,
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const {permission, getUserByIdAtEntry} = useSelector((state) => state.authenticatioauthennSlice);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const [results, setResults] = useState(null);
    const tableId = "tbl_mrht_material_requests_history";
    const searchId = "txt_mrht_search_material_requests_history";
    const leftPosition = 0;

    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes="first_head">{t('request')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('request_type')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('request_by')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('resolved_date')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('status')}</CustomTh>
        ];
    };

    const getTbody = () => {
        if (!results || results?.content?.length === 0) return {
            body: [],
            data: []
        };

        let body = results?.content?.map((item) => {
            // A request already approved is read with its own status
            const statusId = getMaterialRequestTableStatusId(item);
            // The request is read with the form or the vehicle movement it was registered with
            const form = getMaterialControlRequestTypeByType(item?.type);

            return (
                [
                    <CustomTd tableId={tableId} fixed={true} left={leftPosition} classes={"first-column"}>
                        {/* The request is read cut when it does not fit the column,
                            so it is displayed whole in the tooltip of the cell. */}
                        <CustomTooltip
                            text={`${item?.code} — ${item?.title}`}
                            placement={'top'}
                        >
                            <span className={"text-ellipsis text-ellipsis-cell"}>{`${item?.code} — ${item?.title}`}</span>
                        </CustomTooltip>
                    </CustomTd>,
                    <CustomTd tableId={tableId}>
                        <CustomChip
                            background={colors.secondary100}
                            fontColor={colors.secondary700}
                            borderColor={colors.secondary150}
                            legend={t(form.trans)}
                            icon={form.Icon}
                            classNameIcon={"icon-color-secondary-700 h-16px w-16px"}
                        />
                    </CustomTd>,
                    <CustomTd tableId={tableId}>
                        <CustomImageTable
                            tooltip={t(item?.requestedBy)}
                            containerClass={"d-flex"}
                            icon={<div className={`mr-2 d-flex`}><PersonOutlinedChip/></div>}
                        >
                            {t(item?.requestedBy)}
                        </CustomImageTable>
                    </CustomTd>,
                    // The request is registered at a moment of the day, so the
                    // cell reads the date it was asked for and the time it was
                    // registered at, each one with the icon that names it
                    <CustomTd tableId={tableId}>
                        <div className={'d-flex'}>
                            <IconScheduleTable className={'mr-8px mb-1 icon-color-general-950 fs-14px'}/>
                            <p className='fs-14px'>
                                {getDateFormat(item?.createdAt, null, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)}
                            </p>
                        </div>
                        <div className={'d-flex'}>
                            <IconClockTable className={'mr-8px mb-1 icon-color-general-950'}/>
                            <p className='fs-14px'>
                                {getTimeFormat(item?.createdAt)}
                            </p>
                        </div>
                    </CustomTd>,
                    <CustomTd tableId={tableId}>
                        <CustomChip
                            background={statusColor[statusId]}
                            fontColor={statusFontColor[statusId]}
                            borderColor={statusBorderColor[statusId]}
                            legend={t(status[statusId])}
                            variant={'large'}
                        />
                    </CustomTd>
                ]
            );
        });

        return {
            body: body,
            data: results?.content
        };
    };

    const getActionsButtons = (item, index) => {
        // The form the request was registered with is the one its detail is read with
        const form = getMaterialControlRequestTypeByType(item?.type);

        return (
            <DropDownMenuProfile
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                menuId={`menu-${item.id}`}
            >
                {/* The tracker is only read when the request travels an approval flow */}
                {item?.approvalFlowId &&
                <DropDownMenuItem
                    id={'btn_mrht_approval-tracker' + item.id}
                    index={index}
                    name={t('approval_tracker')}
                    url={{
                        pathname: `${routes.approvalTracker.main}${TABLES.MATERIAL_CONTROL_REQUEST}/${item?.id}/${item?.approvalFlowId}`,
                        state: {backUrl: {
                            pathname: routes.materialControl.main,
                            state: {
                                currentTab: MATERIAL_CONTROL_TABS.REQUESTS.INDEX,
                                currentSubView: SUB_VIEW_MODEL.REQUESTS.HISTORY
                            }
                        }}
                    }}
                    icon={<NextIconsOptions/>}
                />
                }
                <DropDownMenuItem
                    id={`dmt_mrht_details_${item.id}`}
                    index={index}
                    handleClose={handleClose}
                    name={t('details')}
                    icon={<DetailsIconsOptions/>}
                    url={{
                        // A vehicle movement is read in its own detail and not in the one of the forms
                        pathname: isVehicleMovement(item)
                            ? routes.vehicleMovements.details + item?.id
                            : routes.materialControl.details + item?.id,
                        state: {
                            formKey: form.key,
                            backUrl: {
                                pathname: routes.materialControl.main,
                                state: {
                                    currentTab: MATERIAL_CONTROL_TABS.REQUESTS.INDEX,
                                    currentSubView: SUB_VIEW_MODEL.REQUESTS.HISTORY
                                }
                            }
                        }
                    }}
                />
            </DropDownMenuProfile>
        );
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page, criteria) => {
        const key = criteria?.searchElement !== undefined ? criteria?.searchElement : searchElement;
        const body = SEARCH_MODEL.setObject({
            page: page,
            size: rowsPerPage,
            model: option,
            key: key,
            userId: getUserByIdAtEntry?.userId,
            view: VIEW_TYPE.LIST.viewType,
        });

        setBodyForExport(body);

        dispatch(SimpleSearchWithId({body})).then(
            ({
                 payload: {
                     data: {data, success},
                 },
             }) => {
                if (success === true) {
                    setResults(data);
                }
            },
        );
    };

    /**
     * Executes a paginated search request using the filters of the advanced search.
     *
     * The filters are read with the same model as the simple search and scoped
     * to the same user, so the table keeps listing the folios the user takes part in.
     */
    const getDataAdvancedSearch = (page) => {
        const body = SEARCH_MODEL.setObject({
            page: page,
            size: rowsPerPage,
            model: option,
            rootGroup: CRITERIA_MODEL.set(finalArray),
            // The search scoped to a user is answered by the custom search, the plain
            // one only takes a search without the user to find
            userToFindId: getUserByIdAtEntry?.userId,
            view: VIEW_TYPE.LIST.viewType,
        });

        setCriteria(body);

        dispatch((body.userToFindId ? SearchByFiltersCustom : SearchByFilters)({ option, body })).then(
            ({
                 payload: {
                     data: { data, success },
                 },
             }) => {
                if (success === true) {
                    setResults(data);
                }
            },
        );
    };

    const {
        thead,
        tbody,
        openMenu,
        handleMenuToggle,
        handleClose,
        pagination,
        rowsPerPage
    } = TableFunction({
        data: results,
        getThead,
        getTbody,
        labelRowsPerPage: 'requests_per_page',
        fixedPagination: false,
        hasContainer: true,
        searchElement, //New
        getData,//New
        getDataAdvancedSearch,
        finalArray,
        modified,//New
        setModified,//New
    });

    return {
        t,
        navigate,
        permission,
        DEV,
        tableId,
        searchId,
        thead,
        tbody,
        getActionsButtons,
        pagination,
        rowsPerPage,
        results,
        setResults,
        getData
    };
};
